/**
 * RTK Query API slice for the Final Trade Confirmation on a transaction (RME-1190).
 *
 * Read-only by design: the confirmation is generated by the platform when a VPPA executes, and
 * neither counterparty may upload over it, replace it, or delete it.
 */
import { baseApi } from '@/store/api/baseApi';
import { ApiTags, TradeConfirmationTagIds } from '@/store/api/tags';

// --- Response Types ---

/** Whether the confirmation was produced at execution time or backfilled later by an admin. */
export type TradeConfirmationSource = 'EXECUTION' | 'BACKFILL';

export interface TradeConfirmationResponse {
  id: number;
  fileName: string;
  source: TradeConfirmationSource;
  generated: string; // ISO 8601
}

export interface TradeConfirmationDownloadResponse {
  url: string;
  documentVersion: number | null;
  filename: string | null;
}

// --- Param Types ---

export interface TradeConfirmationParams {
  negotiationId: number;
  companyId: string;
}

// --- API Slice ---

const BASE_URL = '/api/v1/contracting';

export const tradeConfirmationApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getTradeConfirmation: build.query<
      TradeConfirmationResponse | null,
      TradeConfirmationParams
    >({
      query: ({ negotiationId, companyId }) => ({
        url: `${BASE_URL}/negotiations/${negotiationId}/trade-confirmation`,
        headers: {
          'X-Company-Id': companyId,
        },
        // A 204 is the normal answer for a transaction with no confirmation — non-VPPA deals never
        // get one, and executions predating the generation fix have none until they are backfilled.
        // Without this, fetchBaseQuery tries to parse the empty body as JSON and the query lands in
        // an error state, which would render as "unable to load" rather than as "none yet".
        responseHandler: async response =>
          response.status === 204 ? null : await response.json(),
      }),
      providesTags: (result, error, { negotiationId }) => [
        {
          type: ApiTags.TRADE_CONFIRMATION,
          id: `${TradeConfirmationTagIds.DETAIL}-${negotiationId}`,
        },
      ],
    }),

    getTradeConfirmationDownload: build.query<
      TradeConfirmationDownloadResponse,
      TradeConfirmationParams
    >({
      query: ({ negotiationId, companyId }) => ({
        url: `${BASE_URL}/negotiations/${negotiationId}/trade-confirmation/download`,
        headers: {
          'X-Company-Id': companyId,
        },
      }),
      // The URL is pre-signed and short-lived, so a cached one would hand the user an expired link.
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetTradeConfirmationQuery,
  useLazyGetTradeConfirmationDownloadQuery,
} = tradeConfirmationApi;
